/**
 * useLastShipment — 특정 날짜의 "마지막 출하" 1건(시각 + 차량번호) 조회.
 *
 * daily_shipment_details 캐시(전 사이트 송장 명세)를 구독해, 전 차량 중 시각이 가장
 * 늦은 1대를 골라 { time: "HH:MM", carNo } 로 반환. 데이터 없으면 null.
 *
 * "금일 출하 현황" dense 카드(today2)의 보조줄에 차량번호를 함께 표시하는 용도.
 * (홈 대시보드 + 출하조회 MO 카드 공용)
 */
import { useEffect, useState } from "react";
import { rqcRepo } from "@/services/rqc.repository";

export interface LastShipment {
  /** HH:MM */
  time: string;
  /** 차량번호 */
  carNo: string;
}

/** "08:55" / "0855" → 535(분). 실패 시 null */
function parseTimeText(s: string): number | null {
  if (!s) return null;
  const m = String(s).match(/(\d{1,2})\s*:\s*(\d{2})/);
  if (m) {
    const h = parseInt(m[1], 10), mm = parseInt(m[2], 10);
    if (h >= 0 && h <= 23 && mm >= 0 && mm <= 59) return h * 60 + mm;
  }
  const d = String(s).replace(/[^\d]/g, "");
  if (d.length === 4) {
    const h = parseInt(d.slice(0, 2), 10), mm = parseInt(d.slice(2), 10);
    if (h >= 0 && h <= 23 && mm >= 0 && mm <= 59) return h * 60 + mm;
  }
  return null;
}

export function useLastShipment(date: string): LastShipment | null {
  const [last, setLast] = useState<LastShipment | null>(null);

  useEffect(() => {
    if (!date) {
      setLast(null);
      return;
    }
    const unsub = rqcRepo.subscribeDetailsByDate(
      date,
      (snaps) => {
        let bestMin = -1;
        let best: LastShipment | null = null;
        for (const snap of snaps) {
          for (const d of snap.details) {
            const t = parseTimeText(d.time);
            if (t == null || t <= bestMin) continue;
            bestMin = t;
            best = {
              time: `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`,
              carNo: d.carNo,
            };
          }
        }
        setLast(best);
      },
      () => setLast(null),
    );
    return unsub;
  }, [date]);

  return last;
}
